if (!customElements.get('andcharlie-styling-detail')) {
  customElements.define('andcharlie-styling-detail', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.copyTimer);
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.media = Array.from(this.querySelectorAll('[data-ac-media]'));
      this.mediaIndex = Math.max(0, this.media.findIndex(panel => !panel.hidden));
      this.gesture = null;
      this.suppressSwipeClickUntil = 0;
      this.querySelector('[data-ac-thumbs]')?.removeAttribute('hidden');
      this.querySelector('[data-ac-gallery-controls]')?.removeAttribute('hidden');
      this.querySelector('[data-ac-copy-url]')?.removeAttribute('hidden');
      const copyStatus = this.querySelector('[data-ac-copy-status]');
      if (copyStatus) {
        copyStatus.textContent = '';
        copyStatus.classList.remove('is-visible');
      }

      this.addEventListener('click', event => {
        if (event.detail > 0 && performance.now() < this.suppressSwipeClickUntil && event.target.closest('[data-ac-gallery]') && !event.target.closest('[data-ac-lightbox]')) {
          this.suppressSwipeClickUntil = 0;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const zoom = event.target.closest('[data-ac-zoom]');
        if (zoom && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          if (this.openLightbox(zoom)) event.preventDefault();
          return;
        }
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-ac-media-select')) {
          this.selectMedia(this.media.findIndex(panel => panel.dataset.acMedia === button.dataset.acMediaSelect));
        } else if (button.hasAttribute('data-ac-media-prev')) this.selectMedia(this.mediaIndex - 1);
        else if (button.hasAttribute('data-ac-media-next')) this.selectMedia(this.mediaIndex + 1);
        else if (button.hasAttribute('data-ac-lightbox-close')) this.querySelector('[data-ac-lightbox]')?.close?.();
        else if (button.hasAttribute('data-ac-lightbox-zoom')) {
          const dialog = this.querySelector('[data-ac-lightbox]');
          if (dialog) this.setLightboxZoom(!dialog.classList.contains('is-zoomed'));
        } else if (button.hasAttribute('data-ac-copy-url')) this.copyUrl(button);
        else if (button.classList.contains('ac-styling-detail__related-arrow')) {
          const track = this.querySelector('.ac-styling-detail__related-track');
          const card = track?.firstElementChild;
          if (!card) return;
          const step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
          track.scrollBy({ left: step * (button.classList.contains('ac-styling-detail__related-arrow--prev') ? -1 : 1), behavior: 'instant' });
        }
      }, { signal });

      const gallery = this.querySelector('[data-ac-gallery]');
      gallery?.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.target.closest('[data-ac-lightbox]')) return;
        if (event.target.closest('input, textarea, select, video, audio, [contenteditable]:not([contenteditable="false"])')) return;
        if (this.media.length < 2) return;
        event.preventDefault();
        this.selectMedia(this.mediaIndex + (event.key === 'ArrowLeft' ? -1 : 1));
      }, { signal });
      gallery?.addEventListener('pointerdown', event => {
        this.suppressSwipeClickUntil = 0;
        this.gesture = null;
        if (event.target.closest('[data-ac-lightbox]')) return;
        if (event.pointerType !== 'touch' || !event.isPrimary) {
          this.gesture = null;
          return;
        }
        if (event.target.closest('button, input, textarea, select, video, audio, [contenteditable]:not([contenteditable="false"])')) return;
        if (event.target.closest('a:not([data-ac-zoom])') || this.media.length < 2) return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { signal, passive: true });
      document.addEventListener('pointerup', event => {
        if (!this.gesture || this.gesture.id !== event.pointerId) return;
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        this.gesture = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.suppressSwipeClickUntil = performance.now() + 700;
        this.selectMedia(this.mediaIndex + (dx < 0 ? 1 : -1));
      }, { signal, passive: true });
      document.addEventListener('pointercancel', () => { this.gesture = null; }, { signal, passive: true });

      const dialog = this.querySelector('[data-ac-lightbox]');
      dialog?.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close?.();
      }, { signal });
      dialog?.addEventListener('close', () => {
        this.setLightboxZoom(false);
        if (this.lightboxTrigger?.isConnected) this.lightboxTrigger.focus({ preventScroll: true });
        this.lightboxTrigger = null;
      }, { signal });

      const track = this.querySelector('.ac-styling-detail__related-track');
      track?.addEventListener('scroll', () => this.updateControls(), { signal, passive: true });
      this.addEventListener('ac:styling-loaded', () => this.updateControls(), { signal });
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this);
      if (track) this.resizeObserver.observe(track);
      this.selectMedia(this.mediaIndex);
      this.updateControls();
    }

    selectMedia(index) {
      if (index < 0 || index >= this.media.length) return;
      const focusWillHide = this.media.some((panel, panelIndex) => panelIndex !== index && panel.contains(document.activeElement));
      this.mediaIndex = index;
      const selectedId = this.media[index].dataset.acMedia;
      this.media.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
        if (panel.hidden) panel.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-ac-media-select]').forEach(button => {
        const selected = button.dataset.acMediaSelect === selectedId;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const status = this.querySelector('[data-ac-media-status]');
      if (status) status.textContent = `${index + 1} / ${this.media.length}`;
      const prev = this.querySelector('[data-ac-media-prev]');
      const next = this.querySelector('[data-ac-media-next]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === this.media.length - 1;
      if (focusWillHide) this.querySelector('[data-ac-gallery]')?.focus({ preventScroll: true });
    }

    openLightbox(trigger) {
      const dialog = this.querySelector('[data-ac-lightbox]');
      const image = dialog?.querySelector('[data-ac-lightbox-image]');
      if (!image || typeof dialog.showModal !== 'function') return false;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = trigger.dataset.acZoomUrl || trigger.href;
      image.alt = trigger.dataset.acZoomAlt || trigger.querySelector('img')?.alt || '';
      this.setLightboxZoom(false);
      try {
        dialog.showModal();
        this.lightboxTrigger = trigger;
        return true;
      } catch {
        return false;
      }
    }

    setLightboxZoom(zoomed) {
      const dialog = this.querySelector('[data-ac-lightbox]');
      if (!dialog) return;
      dialog.classList.toggle('is-zoomed', zoomed);
      const button = dialog.querySelector('[data-ac-lightbox-zoom]');
      if (button) {
        button.textContent = zoomed ? '全体を表示' : 'さらに拡大';
        button.setAttribute('aria-pressed', String(zoomed));
      }
      const stage = dialog.querySelector('[data-ac-lightbox-stage]');
      if (stage) stage.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    updateControls() {
      const track = this.querySelector('.ac-styling-detail__related-track');
      if (!track) return;
      const prev = this.querySelector('.ac-styling-detail__related-arrow--prev');
      const next = this.querySelector('.ac-styling-detail__related-arrow--next');
      if (prev) prev.disabled = track.scrollLeft < 1;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }

    async copyUrl(button) {
      const status = this.querySelector('[data-ac-copy-status]');
      if (!status) return;
      const { signal } = this.controller;
      let message;
      try {
        if (!navigator.clipboard) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(button.dataset.acCopyUrl);
        message = 'コピーしました';
      } catch {
        message = 'コピーできませんでした。ブラウザのURLをコピーしてください。';
      }
      if (signal.aborted || !this.isConnected) return;
      status.textContent = message;
      status.classList.add('is-visible');
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        status.classList.remove('is-visible');
        status.textContent = '';
      }, 4000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.copyTimer);
      this.gesture = null;
      this.lightboxTrigger = null;
      this.querySelector('[data-ac-lightbox]')?.close?.();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}
